// ─────────────────────────────────────────────
// Oike · Mi espacio › Mi marca — formats, original templates, the approved
// identity and the pure checks (contrast, legibility, crops, time zones).
//
// The identity here is ONLY for the materials of Ale's personal brand
// (Alejandra Cadario). Oike's screens, proposals and forms keep their own
// design: nothing outside src/components/owner/brand/ reads these colors.
//
// Templates are originals defined in code, with a version: a piece copies what
// it needs and keeps the template key and version, so editing a piece never
// changes its template, and a new template version never changes a saved piece.
// Pure: shared by the screens, owner-space.mts (shared/ownerBrand.ts) and the tests.
// ─────────────────────────────────────────────

// ── The approved identity (version 1 of the brand profile) ──

export const BRAND_COLOR_ROLES = [
  { key: 'forest', label: 'Verde bosque', use: 'Títulos y fondos principales' },
  { key: 'ivory', label: 'Marfil', use: 'Fondo despejado' },
  { key: 'chocolate', label: 'Chocolate', use: 'Texto y fondos cálidos' },
  { key: 'mustard', label: 'Mostaza', use: 'Acentos discretos (líneas, detalles)' },
];

export const DEFAULT_BRAND_PROFILE = {
  name: 'Alejandra Cadario',
  positioning: 'Acompaño la conexión entre el desarrollo personal y el profesional en etapas de cambio. La migración aporta mi experiencia, pero hablo también a quienes atraviesan otros cambios de vida y de trabajo.',
  audience: 'Personas en etapas de cambio (migración, cambio de trabajo o de rol, nuevas etapas de vida) que quieren reconocerse en sus retos y descubrir posibilidades y capacidades propias.',
  values: ['Honestidad', 'Resiliencia', 'Raíces', 'Pertenencia', 'Humor', 'Acciones concretas'],
  voice: [
    'Voseo coherente en todo el texto.',
    'Lenguaje cercano, franco y expresivo.',
    'Que las personas se reconozcan en los retos y descubran posibilidades y capacidades propias.',
    'Cerrar con una acción concreta y posible.',
  ],
  avoid: [
    'Promesas garantizadas o resultados asegurados.',
    'Experiencias inventadas: solo lo que viví o lo que me contaron con permiso.',
    'Convertir mi historia en una receta universal.',
    'Apropiarme de historias o testimonios de otras personas: siempre con su autoría.',
  ],
  palette: { forest: '#294336', ivory: '#F7F2E9', chocolate: '#432A26', mustard: '#CB9E27' },
  fonts: { title: 'Georgia', body: 'Arial' },
  visual: [
    'Fondos despejados.',
    'Jerarquía clara: un título, una idea, una acción.',
    'Acentos mostaza discretos.',
  ],
};

export const BRAND_FONT_STACKS = {
  Georgia: "Georgia, 'Times New Roman', Times, serif",
  Arial: "Arial, 'Helvetica Neue', Helvetica, 'Liberation Sans', sans-serif",
  // Thick sans serif of the YouTube and LinkedIn collection (headlines only). The
  // font file ships with Oike (public/fonts/montserrat, SIL OFL) and is loaded
  // before drawing, so the catalog, the editor and the PNG measure the same letters.
  Montserrat: "'Montserrat', 'Arial Black', Arial, sans-serif",
};
// The fonts Ale picks in her profile (the collection's headline font is fixed by its templates)
export const BRAND_FONTS = ['Georgia', 'Arial'];
export const fontStack = (name) => BRAND_FONT_STACKS[name] || BRAND_FONT_STACKS.Arial;

export const BRAND_TEXT_LIMITS = {
  name: 80, positioning: 1200, audience: 1200, listItem: 240, listMax: 12, note: 500,
  title: 140, slot: 600, caption: 2200, brief: 1500, reflection: 3000, quote: 600, author: 120,
  personName: 120, personRole: 140, credit: 160, authorizationNote: 600, photoName: 120,
};
export const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

// ── Contrast (WCAG 2.x) ──

const channel = (c) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
export function luminance(hex) {
  const n = parseInt(String(hex).slice(1), 16);
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
}
export function contrastRatio(a, b) {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p);
  return Math.round(((x + 0.05) / (y + 0.05)) * 100) / 100;
}
export const AA_NORMAL = 4.5;
export const AA_LARGE = 3;

/** Every text/background pair of the palette, with what it is good for. */
export function contrastMatrix(palette) {
  const out = [];
  for (const fg of BRAND_COLOR_ROLES) for (const bg of BRAND_COLOR_ROLES) {
    if (fg.key === bg.key) continue;
    const ratio = contrastRatio(palette[fg.key], palette[bg.key]);
    out.push({ fg: fg.key, bg: bg.key, ratio, normal: ratio >= AA_NORMAL, large: ratio >= AA_LARGE });
  }
  return out;
}

// ── Color schemes of the templates (by palette role, so an edited palette is re-checked) ──
// accent is decorative (lines, shapes); a text in the accent color is used only where
// its contrast passes for its size, otherwise the text takes the title color.
// v1 (the first templates): Ale picked one of these per piece. Since v2, every piece
// uses BRAND_STYLE (the approved direction) and the choice is gone.
export const BRAND_SCHEMES = {
  ivory: { label: 'Fondo marfil', bg: 'ivory', title: 'forest', body: 'chocolate', accent: 'mustard', panel: 'forest', onPanel: 'ivory' },
  forest: { label: 'Fondo verde bosque', bg: 'forest', title: 'ivory', body: 'ivory', accent: 'mustard', panel: 'ivory', onPanel: 'forest' },
  chocolate: { label: 'Fondo chocolate', bg: 'chocolate', title: 'ivory', body: 'ivory', accent: 'mustard', panel: 'ivory', onPanel: 'chocolate' },
};

/**
 * The approved style (templates v2, 27-10-2026): forest green background, ivory
 * titles and texts, discreet mustard details. Chocolate only behind ivory text
 * (initials), never as text on green. Photos keep their own colors.
 */
export const BRAND_STYLE = { label: 'Estilo aprobado', bg: 'forest', title: 'ivory', body: 'ivory', accent: 'mustard', panel: 'chocolate', onPanel: 'ivory' };
export const STYLE_VERSION = 2;
/** The colors of a piece: the approved style from v2 on (and always in the collection); the scheme Ale chose in a v1 piece. */
export const schemeOf = (piece) => (BRAND_TEMPLATES[piece?.template]?.collection || (piece?.templateVersion || 1) >= STYLE_VERSION ? BRAND_STYLE : BRAND_SCHEMES[piece?.content?.scheme] || BRAND_SCHEMES.ivory);

// ── Formats (exact export size) and the channel's safe zones ──
// viewWidth: the width (CSS px) at which the piece is usually seen on a phone or in
// a list. Legibility is checked at that size, not at the export size.
// cover: what the channel covers or crops (the overlay shows it; it is never exported).
export const BRAND_FORMATS = {
  ig_post: {
    label: 'Instagram · vertical 4:5', width: 1080, height: 1350, viewWidth: 390,
    margin: 80,
    cover: [
      { x: 0, y: 0, w: 34, h: 1350, label: 'La cuadrícula del perfil (3:4) recorta este borde' },
      { x: 1046, y: 0, w: 34, h: 1350, label: 'La cuadrícula del perfil (3:4) recorta este borde' },
    ],
    note: 'En el perfil se ve recortada a 3:4: los bordes laterales pueden no verse.',
  },
  ig_story: {
    label: 'Historia vertical 9:16', width: 1080, height: 1920, viewWidth: 390,
    margin: 80,
    cover: [
      { x: 0, y: 0, w: 1080, h: 250, label: 'Barra de progreso, perfil y cerrar' },
      { x: 0, y: 1580, w: 1080, h: 340, label: 'Responder, reacciones y enlaces' },
    ],
    note: 'Arriba y abajo los controles de la historia tapan el contenido.',
  },
  ig_square: {
    label: 'Instagram · cuadrada 1:1', width: 1080, height: 1080, viewWidth: 390,
    margin: 72,
    cover: [],
    note: 'En el feed se ve completa; en la cuadrícula del perfil se recorta a 3:4 por los costados.',
  },
  ig_carousel: {
    label: 'Instagram · carrusel 4:5', width: 1080, height: 1350, viewWidth: 390,
    margin: 80,
    cover: [
      { x: 0, y: 0, w: 34, h: 1350, label: 'La cuadrícula del perfil (3:4) recorta este borde de la portada' },
      { x: 1046, y: 0, w: 34, h: 1350, label: 'La cuadrícula del perfil (3:4) recorta este borde de la portada' },
    ],
    note: 'Láminas de 1080 × 1350. En el perfil solo se ve la portada, recortada a 3:4.',
  },
  youtube: {
    label: 'YouTube · miniatura 16:9', width: 1280, height: 720, viewWidth: 320,
    margin: 64,
    cover: [{ x: 1080, y: 630, w: 200, h: 90, label: 'Duración del video' }],
    note: 'Se ve muy chica en listas y celulares, con la duración abajo a la derecha.',
  },
  article: {
    label: 'Portada de artículo o newsletter', width: 1200, height: 630, viewWidth: 500,
    margin: 64,
    cover: [
      { x: 0, y: 0, w: 285, h: 630, label: 'Algunas vistas previas la recortan al cuadrado' },
      { x: 915, y: 0, w: 285, h: 630, label: 'Algunas vistas previas la recortan al cuadrado' },
    ],
    note: 'Tamaño de vista previa de enlaces (1,91:1). Algunas apps la muestran cuadrada, al centro.',
  },
  li_post: {
    label: 'LinkedIn · vertical 4:5', width: 1080, height: 1350, viewWidth: 360,
    margin: 72,
    cover: [],
    note: 'En el feed del celular ocupa el ancho de la pantalla (unos 360 px); en escritorio se ve algo más grande.',
  },
  li_square: {
    label: 'LinkedIn · cuadrada 1:1', width: 1080, height: 1080, viewWidth: 360,
    margin: 72,
    cover: [],
    note: 'En el feed del celular ocupa el ancho de la pantalla (unos 360 px).',
  },
  // LinkedIn document (Omni added the option «li_document» to Personal Brand Pieces › Format on 04-11-2026, field
  // fldjljjePkCA6Oclk, option sel1RnB8czPkOKWPK): pages of 1080 × 1350 downloaded as one PDF (LinkedIn's carousel).
  li_document: {
    label: 'LinkedIn · Documento PDF', width: 1080, height: 1350, viewWidth: 360, margin: 88, cover: [], document: true,
    note: 'Páginas 4:5 en un PDF con texto seleccionable: LinkedIn lo muestra como carrusel.',
  },
  // TikTok video cover (Omni added the option «tiktok_cover» to Personal Brand Pieces › Format on 04-11-2026, option
  // selLibYG9lFh01wlr): an image, apart from the TikTok script («tiktok»). Seen in the profile grid at about 130 px.
  tiktok_cover: {
    label: 'TikTok · Portada de video', width: 1080, height: 1920, viewWidth: 130, margin: 80, cover: [],
    note: 'Imagen vertical para usar como portada del video. En el perfil se ve recortada (3:4, orientativo) y muy chica.',
  },
  landscape: {
    label: 'Horizontal 16:9', width: 1920, height: 1080, viewWidth: 560,
    margin: 96,
    cover: [],
    note: 'Para LinkedIn, correo o una presentación.',
  },  // TikTok (Omni added the option «tiktok» to Personal Brand Pieces › Format on 04-11-2026): a script to record, not an
  // image. The same video can also go to Instagram Reels and YouTube Shorts (publication channels of this piece).
  tiktok: {
    label: 'TikTok · video vertical 9:16', width: 1080, height: 1920, viewWidth: 390, margin: 80, cover: [], script: true,
    note: 'Guion para grabar el video: no se exporta como imagen. El mismo video sirve para Reels y Shorts.',
  },
  // PDF A4 (Omni added the option «pdf_a4» to Personal Brand Pieces › Format on 04-11-2026): a document of several
  // pages in points (1 pt = 1/72 in). Drawn by src/notebook.js, never by the image renderer.
  pdf_a4: {
    label: 'PDF · A4', width: 595.28, height: 841.89, viewWidth: 595, margin: 48, cover: [], document: true,
    note: 'Documento de varias páginas: se descarga como PDF con texto seleccionable.',
  },
};

// Minimum size a text should have at the usual viewing size (CSS px)
export const LEGIBILITY_MIN = { headline: 16, body: 10.5, small: 9 };
/** Large text for WCAG (≥ 24 px, or ≥ 18.66 px bold) at the usual viewing size. */
export const isLargeText = (px, format, bold = false) => {
  const shown = px * (BRAND_FORMATS[format].viewWidth / BRAND_FORMATS[format].width);
  return shown >= 24 || (bold && shown >= 18.66);
};
export const shownSize = (px, format) => Math.round(px * (BRAND_FORMATS[format].viewWidth / BRAND_FORMATS[format].width) * 10) / 10;

// ── The collection "YouTube y LinkedIn" ──
// Channel → formats. The piece keeps its format; the channel comes from it.
export const BRAND_CHANNELS = {
  youtube: { label: 'YouTube', formats: ['youtube'] },
  linkedin: { label: 'LinkedIn', formats: ['li_post', 'li_square'] },
  instagram: { label: 'Instagram', formats: ['ig_post', 'ig_square', 'ig_carousel'] },
};
export const channelOf = (format) => Object.keys(BRAND_CHANNELS).find(c => BRAND_CHANNELS[c].formats.includes(format)) || null;
const SOCIAL_FORMATS = ['youtube', 'li_post', 'li_square', 'ig_post', 'ig_square', 'ig_carousel'];
// Instagram carousel of the collection: the cover is the design with its people; then
// up to 9 slides of text (the last one closes with the signature). 2 to 10 slides.
export const SOCIAL_SLIDES = { min: 2, max: 10 };
export const isCarouselFormat = (format) => format === 'ig_carousel';
// The people of a piece: Ale and up to three guests, each with a photo of their own
const SOCIAL_PEOPLE = [
  { key: 'self', label: 'Tu foto', self: true },
  { key: 'guest1', label: 'Invitado/a 1' },
  { key: 'guest2', label: 'Invitado/a 2' },
  { key: 'guest3', label: 'Invitado/a 3' },
];
export const SOCIAL_GUESTS_MAX = 3;
export const SOCIAL_CASTS = [
  { key: 'self', label: 'Solo mi foto' },
  { key: 'guests', label: 'Solo invitados' },
  { key: 'both', label: 'Mi foto con invitados' },
];
export const SOCIAL_ALIGNS = [{ key: 'left', label: 'Izquierda' }, { key: 'center', label: 'Centro' }, { key: 'right', label: 'Derecha' }];
export const SOCIAL_SIDES = [{ key: 'right', label: 'Fotos a la derecha' }, { key: 'left', label: 'Fotos a la izquierda' }];
export const HALO_COLORS = [{ key: 'ivory', label: 'Marfil' }, { key: 'mustard', label: 'Mostaza' }];
export const SOCIAL_STYLE_DEFAULTS = { align: 'left', side: 'right', halo: true, haloColor: 'ivory', decor: true, highlights: [] };
export const SOCIAL_HIGHLIGHTS_MAX = 8;
// Ale's photos are framed from the waist up: her waist sits on the lower edge of
// the photo place and only a little air above the head can change (never a
// close-up of the face). head and waist: heights in the photo, 0 (top) to 1 (bottom).
export const SELF_FRAME_DEFAULT = { head: 0.06, waist: 1 };
export const SELF_ZOOM = { min: 0.85, max: 1.1 };
export const GUEST_ZOOM = { min: 1, max: 4 };

/** The people shown, in order (Ale first), from who appears and how many guests. */
export function activePeople(content = {}) {
  const cast = SOCIAL_CASTS.some(c => c.key === content.cast) ? content.cast : 'self';
  const n = Math.min(SOCIAL_GUESTS_MAX, Math.max(1, Math.round(Number(content.guests) || 1)));
  const guests = Array.from({ length: n }, (_, i) => `guest${i + 1}`);
  return cast === 'self' ? ['self'] : cast === 'guests' ? guests : ['self', ...guests];
}
export const isCollection = (templateKey) => BRAND_TEMPLATES[templateKey]?.collection === 'social';
/** A word as it is compared for highlighting (case and accents do not matter, punctuation is ignored). */
export const highlightKey = (w) => String(w || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^\p{L}\p{N}]+/gu, '');

/**
 * Where Ale's photo goes in its place, from the waist up: one scale for both axes
 * (never deformed), the waist on the lower edge, the head with some air above.
 * zoom changes only that air (SELF_ZOOM). x moves the photo sideways.
 * A photo with background must fill its place: when it is narrower or shorter than
 * the place, the place shrinks to the photo (never enlarged past the waist rule).
 * Returns { rect, box } (box: the place actually used).
 */
export function selfFrameRect(img, box, frame = {}, crop = {}) {
  const head = Math.min(0.8, Math.max(0, Number(frame.head ?? SELF_FRAME_DEFAULT.head)));
  const waist = Math.min(1, Math.max(head + 0.15, Number(frame.waist ?? SELF_FRAME_DEFAULT.waist)));
  const zoom = Math.min(SELF_ZOOM.max, Math.max(SELF_ZOOM.min, Number(crop.zoom) || 1));
  const fx = Math.min(1, Math.max(0, crop.x ?? 0.5));
  const scale = (box.h * 0.88 * zoom) / ((waist - head) * img.h);
  const w = img.w * scale, h = img.h * scale;
  const bottom = box.y + box.h;
  const y = bottom - waist * h;
  let x = box.x + box.w / 2 - fx * w;
  // Keep the photo over its place when it is wide enough
  if (w >= box.w) x = Math.min(box.x, Math.max(box.x + box.w - w, x));
  const used = { ...box };
  if (w < box.w) { used.w = w; used.x = Math.min(box.x + box.w - w, Math.max(box.x, x)); x = used.x; }
  if (y > box.y) { used.y = y; used.h = bottom - y; }
  return { rect: { x, y, w, h, scale }, box: used };
}

// ── Original templates (never edited from the app; a new version is a code change) ──
// slots: the texts of the piece. photos: the photo places. A template with photos
// still works without them (typographic composition).
const S = (key, label, max, extra = {}) => ({ key, label, max, ...extra });
export const BRAND_TEMPLATES = {
  // Earlier identity, retired from the catalog on 04-11-2026 (replaced by the Pizarra templates): saved pieces keep
  // opening, editing and downloading with their original design. Retiring never deletes a record or a version.
  post: {
    version: 2, retired: true, label: 'Publicación vertical', formats: ['ig_post'],
    description: 'Una idea, un título y una acción. Con foto arriba o solo tipografía.',
    slots: [S('kicker', 'Antetítulo', 40), S('headline', 'Título', 90, { required: true }), S('body', 'Texto', 220), S('signature', 'Firma', 60)],
    photos: [{ key: 'main', label: 'Foto principal' }],
    defaults: { kicker: '', headline: '', body: '', signature: 'Alejandra Cadario' },
  },
  carousel: {
    version: 2, retired: true, label: 'Carrusel', formats: ['ig_post'], slides: { min: 2, max: 10 },
    description: 'Portada, láminas de contenido y cierre, todas de 1080 × 1350.',
    slots: [S('headline', 'Título de la lámina', 90, { required: true }), S('body', 'Texto de la lámina', 260)],
    photos: [{ key: 'main', label: 'Foto de la portada' }],
    defaults: { slides: [{ kind: 'cover', headline: '', body: '' }, { kind: 'content', headline: '', body: '' }, { kind: 'closing', headline: '', body: '' }], signature: 'Alejandra Cadario' },
  },
  // Earlier YouTube designs, retired from the catalog on 04-11-2026 (replaced by the Pizarra thumbnail): saved pieces keep
  // opening, editing and downloading with their design. Retiring never deletes a record or a version.
  youtube: {
    version: 2, retired: true, label: 'Miniatura de YouTube', formats: ['youtube'],
    description: 'Pocas palabras, muy grandes. La duración del video tapa la esquina inferior derecha.',
    slots: [S('kicker', 'Antetítulo', 30), S('headline', 'Título corto', 48, { required: true })],
    photos: [{ key: 'main', label: 'Foto' }],
    defaults: { kicker: '', headline: '' },
  },
  // Earlier article cover, retired from the catalog on 04-11-2026 (replaced by the Pizarra «LinkedIn · Portada de
  // artículo», pz_li_article): saved pieces keep opening, editing and downloading at 1200 × 630 with this design
  article: {
    version: 2, retired: true, label: 'Portada de artículo o newsletter', formats: ['article'],
    description: 'Título editorial, bajada y firma.',
    slots: [S('kicker', 'Sección o serie', 40), S('headline', 'Título', 110, { required: true }), S('body', 'Bajada', 160), S('signature', 'Firma', 60)],
    photos: [{ key: 'main', label: 'Foto' }],
    defaults: { kicker: '', headline: '', body: '', signature: 'Por Alejandra Cadario' },
  },
  webinar: {
    version: 2, label: 'Invitación a webinar', formats: ['ig_post', 'ig_story', 'landscape'],
    description: 'Título, fecha y hora con su zona horaria, invitados y cómo inscribirse. Los datos salen del evento de Oike.',
    slots: [S('kicker', 'Antetítulo', 40), S('headline', 'Título', 90, { required: true }), S('body', 'Bajada', 180), S('cta', 'Llamado a inscribirse', 70)],
    photos: [{ key: 'host', label: 'Tu foto' }, { key: 'guest1', label: 'Invitado/a 1' }, { key: 'guest2', label: 'Invitado/a 2' }, { key: 'guest3', label: 'Invitado/a 3' }],
    defaults: { kicker: 'Webinar gratuito', headline: '', body: '', cta: 'Inscribite con el enlace del texto' },
  },
  // ── Collection "YouTube y LinkedIn" (27-10-2026): four designs, each in both channels ──
  // Editable pieces, never pictures with the text burned in: texts, people, photos,
  // crops, alignment, highlighted words, halo and details are kept in the piece.
  explainer: {
    version: 1, collection: 'social', retired: true, catalogFormats: [], label: 'Explicación', formats: SOCIAL_FORMATS,
    description: 'Foto recortada a un lado y título grande al otro.',
    slots: [S('kicker', 'Antetítulo', 32), S('headline', 'Título', 70, { required: true })],
    photos: SOCIAL_PEOPLE, decor: 'Barra mostaza junto al título y puntos detrás de la foto',
    defaults: { kicker: '', headline: '' },
  },
  highlight: {
    version: 1, collection: 'social', retired: true, catalogFormats: [], label: 'Palabras destacadas', formats: SOCIAL_FORMATS,
    description: 'El título repartido en bloques de color, apenas inclinados. Las palabras que elijas van en mostaza.',
    slots: [S('kicker', 'Antetítulo', 32), S('headline', 'Título', 60, { required: true })],
    photos: SOCIAL_PEOPLE, decor: 'Trazos mostaza junto a los bloques',
    defaults: { kicker: '', headline: '' },
  },
  idea: {
    version: 1, collection: 'social', retired: true, catalogFormats: [], label: 'Idea o pregunta', formats: SOCIAL_FORMATS,
    description: 'El título es protagonista; una foto expresiva lo acompaña. Detalles gráficos opcionales.',
    slots: [S('kicker', 'Antetítulo', 32), S('headline', 'Idea o pregunta', 80, { required: true })],
    photos: SOCIAL_PEOPLE, decor: 'Signo grande (pregunta o comillas) y círculo mostaza detrás de la foto',
    defaults: { kicker: '', headline: '' },
  },
  blocks: {
    version: 1, collection: 'social', retired: true, catalogFormats: [], label: 'Bloques mostaza', formats: SOCIAL_FORMATS,
    description: 'Títulos grandes sobre bloques mostaza, foto recortada y pequeños dibujos (la referencia aprobada).',
    slots: [S('kicker', 'Antetítulo', 32), S('headline', 'Título', 60, { required: true })],
    photos: SOCIAL_PEOPLE, decor: 'Pequeños dibujos: lamparita y nube de pensamiento',
    // As in the approved reference: the name on top, no outline around the photo
    defaults: { kicker: 'Alejandra Cadario', headline: '' }, styleDefaults: { halo: false },
  },
  // A script, not a canvas: hook, development and close, plus what appears on screen. No photos, no PNG.
  tiktok: {
    version: 1, script: true, label: 'Guion de TikTok', formats: ['tiktok'],
    description: 'Apertura, desarrollo y cierre con la llamada a la acción, y el texto que acompaña el video.',
    slots: [S('hook', 'Apertura (primeros segundos)', 150, { required: true }), S('body', 'Desarrollo', 900, { required: true }), S('close', 'Cierre con la llamada a la acción', 250, { required: true }), S('onscreen', 'Texto en pantalla (opcional)', 120)],
    photos: [],
    defaults: { hook: '', body: '', close: '', onscreen: '' },
  },
  podcast: {
    version: 1, collection: 'social', retired: true, catalogFormats: [], label: 'Podcast o entrevista', formats: SOCIAL_FORMATS,
    description: 'Foto, título y, si querés, el programa, el episodio y el nombre y rol de cada persona.',
    slots: [S('kicker', 'Programa', 40), S('episode', 'Episodio', 24), S('headline', 'Título', 70, { required: true })],
    photos: SOCIAL_PEOPLE, decor: 'Ondas de sonido junto al programa y un aro detrás de las personas',
    defaults: { kicker: '', episode: '', headline: '' },
  },
  // «Cuaderno de trabajo» (04-11-2026): the first format of identity A «Pizarra» (src/brandIdentity.js). Its pages
  // and rules live in src/notebook.js; it has no slots or photos of its own (the cover image is inside its content).
  // Instagram and LinkedIn with identity A «Pizarra» (approved by Ale on 04-11-2026, v3 samples): one size per format,
  // two compositions over the same content. Layout and rules in src/pizarraSocial.js; edited in PizarraEditor.
  pz_ig_post: { version: 1, pizarra: true, identity: 'pizarra', label: 'Instagram · Post', formats: ['ig_post'], description: 'Una idea con título, texto, tres puntos y una anotación. Foto opcional.', slots: [], photos: [], defaults: {} },
  pz_ig_carousel: { version: 1, pizarra: true, identity: 'pizarra', label: 'Instagram · Carrusel', formats: ['ig_carousel'], description: 'Portada, pasos y cierre; se descargan todas las láminas.', slots: [], photos: [], defaults: {} },
  pz_ig_story: { version: 1, pizarra: true, identity: 'pizarra', label: 'Instagram · Story', formats: ['ig_story'], description: 'Una pregunta, tres puntos y una invitación a responder, dentro de la zona segura.', slots: [], photos: [], defaults: {} },
  // li_post (4:5) only for a weekly plan's content saved before the renewal (its format and task keys never change)
  pz_li_post: { version: 1, pizarra: true, identity: 'pizarra', label: 'LinkedIn · Post con imagen', formats: ['li_square', 'li_post'], catalogFormats: ['li_square'], description: 'Título, texto y un esquema de tres palabras. Foto opcional.', slots: [], photos: [], defaults: {} },
  pz_youtube: { version: 1, pizarra: true, identity: 'pizarra', label: 'YouTube · Miniatura', formats: ['youtube'], description: 'Título breve y grande, foto opcional y un trazo que destaca la idea; se lee en pequeño.', slots: [], photos: [], defaults: {} },
  // LinkedIn article or newsletter cover: 1920 × 1080 (16:9), the existing format «landscape» (the template tells it from
  // the webinar invitation, which also uses «landscape»); only the cover, never the body of the article
  pz_li_article: { version: 1, pizarra: true, identity: 'pizarra', label: 'LinkedIn · Portada de artículo', formats: ['landscape'], description: 'Portada de un artículo o de una edición del newsletter: sección, título, bajada y firma. Foto opcional.', slots: [], photos: [], defaults: {} },
  pz_tiktok_cover: { version: 1, pizarra: true, identity: 'pizarra', label: 'TikTok · Portada de video', formats: ['tiktok_cover'], description: 'Imagen vertical para la portada del video, con la vista del recorte del perfil. Distinta del guion.', slots: [], photos: [], defaults: {} },
  pz_li_document: { version: 1, pizarra: true, identity: 'pizarra', label: 'LinkedIn · Documento PDF', formats: ['li_document'], description: 'Portada, lectura y preguntas en páginas 4:5; un PDF con texto seleccionable.', slots: [], photos: [], defaults: {} },
  cuaderno: {
    version: 1, document: true, identity: 'pizarra', label: 'Cuaderno de trabajo', formats: ['pdf_a4'],
    description: 'PDF de varias páginas: portada con o sin tu imagen, lectura, esquema y ejercicio, con la identidad Pizarra.',
    slots: [], photos: [], defaults: {},
  },
};
export const TEMPLATE_KEYS = Object.keys(BRAND_TEMPLATES);
/** A script template (TikTok): edited and approved like any piece, but never drawn or exported as an image. */
export const isScriptTemplate = (key) => !!BRAND_TEMPLATES[key]?.script;
/** A document template (the PDF notebook): edited, approved and downloaded as a multi-page PDF, never as an image. */
export const isDocumentTemplate = (key) => !!BRAND_TEMPLATES[key]?.document;
/** An Instagram or LinkedIn template of identity A «Pizarra» (its own editor, src/pizarraSocial.js). */
export const isPizarraKey = (key) => !!BRAND_TEMPLATES[key]?.pizarra;
/** Templates offered to create a new piece of a format (retired ones keep working for saved pieces, never offered). */
export const catalogTemplates = (format) => Object.entries(BRAND_TEMPLATES).filter(([, t]) => !t.retired && (t.catalogFormats || t.formats).includes(format)).map(([k]) => k);
/** Where the same TikTok video can be published (one piece, several channels; each publication is registered apart). */
export const VIDEO_CHANNELS = [{ key: 'tiktok', label: 'TikTok' }, { key: 'reels', label: 'Instagram Reels' }, { key: 'shorts', label: 'YouTube Shorts' }];
/** The texts of each slide after the cover, in a carousel of the collection. */
export const SOCIAL_SLIDE_SLOTS = [S('headline', 'Título de la lámina', 70, { required: true }), S('body', 'Texto de la lámina', 260)];
/** How many slides a piece has (1 unless it is a carousel). */
export function slideCountOf(piece) {
  const tpl = BRAND_TEMPLATES[piece?.template];
  if (!tpl) return 1;
  if (tpl.slides) return (piece.content?.slides || []).length || 1;
  if (tpl.collection && isCarouselFormat(piece.format)) return 1 + (piece.content?.slides || []).length;
  return 1;
}
export const templateOf = (key) => BRAND_TEMPLATES[key] || null;

export const PIECE_STATUSES = [
  { key: 'draft', label: 'Borrador' },
  { key: 'approved', label: 'Aprobada' },
  { key: 'archived', label: 'Archivada' },
];

// ── Photos ──

export const PHOTO_KINDS = [
  { key: 'self', label: 'Foto mía' },
  { key: 'guest', label: 'Invitado/a' },
  { key: 'other', label: 'Otra (con crédito)' },
];
export const PHOTO_AUTHORIZATIONS = [
  { key: 'own', label: 'Propia (no hace falta autorización)' },
  { key: 'authorized', label: 'Autorizada' },
  { key: 'pending', label: 'Autorización pendiente' },
  { key: 'denied', label: 'No autorizada' },
];
export const PHOTO_MAX_SIDE = 2000;           // the browser scales down (never up) before sending
export const PHOTO_MAX_BYTES = 3_000_000;     // after scaling, as JPEG
export const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/** Whether a photo may appear in an approved or exported piece. */
export function photoUsable(photo) {
  if (!photo || photo.archived) return { ok: false, reason: 'missing' };
  if (photo.kind === 'self') return { ok: true };
  if (photo.authorization !== 'authorized') return { ok: false, reason: 'not_authorized' };
  if (!photo.personName && photo.kind === 'guest') return { ok: false, reason: 'no_name' };
  if (photo.kind === 'other' && !photo.credit) return { ok: false, reason: 'no_credit' };
  return { ok: true };
}

/**
 * Where a photo is drawn to fill a box without deforming it: one scale for both
 * axes (cover), the focal point as close to the center as the edges allow.
 * crop: { x, y } focal point in 0..1 of the image, zoom ≥ 1.
 */
export function coverRect(img, box, crop = {}) {
  const zoom = Math.min(4, Math.max(1, Number(crop.zoom) || 1));
  const fx = Math.min(1, Math.max(0, crop.x ?? 0.5)), fy = Math.min(1, Math.max(0, crop.y ?? 0.5));
  const scale = Math.max(box.w / img.w, box.h / img.h) * zoom;
  const w = img.w * scale, h = img.h * scale;
  const x = Math.min(box.x, Math.max(box.x + box.w - w, box.x + box.w / 2 - fx * w));
  const y = Math.min(box.y, Math.max(box.y + box.h - h, box.y + box.h / 2 - fy * h));
  return { x, y, w, h, scale };
}

// ── Webinars: date, time and time zone, always explicit ──

const isTz = (tz) => { try { new Intl.DateTimeFormat('es', { timeZone: tz }); return !!tz; } catch { return false; } };
/** GMT offset of a zone at an instant, as "GMT-3" or "GMT+5:30". */
export function gmtOffset(instant, tz) {
  try {
    const p = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(new Date(instant));
    const v = p.find(x => x.type === 'timeZoneName')?.value || '';
    return v === 'GMT' ? 'GMT+0' : v;
  } catch { return ''; }
}
/** The instant (UTC ISO) of a local date and time in a zone. */
export function zonedInstant(date, time, tz) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '') || !/^\d{2}:\d{2}$/.test(time || '') || !isTz(tz)) return null;
  const guess = Date.parse(`${date}T${time}:00Z`);
  let t = guess;
  for (let i = 0; i < 3; i++) {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(t)).map(x => [x.type, x.value]));
    const shown = Date.parse(`${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:00Z`);
    t += guess - shown;
  }
  return new Date(t).toISOString();
}
const CITY_ES = { Mexico_City: 'Ciudad de México', Sao_Paulo: 'São Paulo', New_York: 'Nueva York', Los_Angeles: 'Los Ángeles', Bogota: 'Bogotá', Asuncion: 'Asunción', London: 'Londres', Berlin: 'Berlín', Rome: 'Roma', Lisbon: 'Lisboa', Panama: 'Panamá', Havana: 'La Habana', Zurich: 'Zúrich' };
const cityOf = (tz) => { const c = String(tz).split('/').pop(); return CITY_ES[c] || c.replace(/_/g, ' '); };

/**
 * The when of a webinar for a piece. The zone is always written; without a time
 * or a zone the line says what is missing instead of guessing.
 * w: { date, startsAt?, time?, timeZone?, extraZones? }
 */
export function webinarWhen(w = {}) {
  const tz = isTz(w.timeZone) ? w.timeZone : '';
  const instant = w.startsAt || (w.date && w.time && tz ? zonedInstant(w.date, w.time, tz) : null);
  const missing = [];
  if (!w.date && !w.startsAt) missing.push('date');
  if (!instant) missing.push('time');
  if (!tz) missing.push('timeZone');
  if (!instant || !tz) {
    const day = w.date ? new Date(`${w.date}T12:00:00Z`).toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }) : '';
    return { line: day ? cap(day) : '', zones: [], missing, instant: null };
  }
  const d = new Date(instant);
  const day = d.toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long', timeZone: tz });
  const zone = (z) => ({ tz: z, city: cityOf(z), time: d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: z }), offset: gmtOffset(instant, z),
    day: d.toLocaleDateString('es', { weekday: 'short', day: 'numeric', timeZone: z }) });
  const main = zone(tz);
  const extra = (Array.isArray(w.extraZones) ? w.extraZones : []).filter(z => isTz(z) && z !== tz).slice(0, 3).map(zone);
  return {
    line: `${cap(day)} · ${main.time} h (${main.city}, ${main.offset})`,
    zones: [main, ...extra],
    extraLine: extra.map(z => `${z.time} h ${z.city}${z.day !== main.day ? ` (${z.day})` : ''}`).join(' · '),
    missing, instant,
  };
}
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// ── Material origins (what the screen and the prompt call each kind of text) ──
export const MATERIAL_ORIGINS = {
  S: { label: 'Contenido de la fuente', note: 'Texto de un libro, artículo o material que guardaste (fragmentos o contenido procesado).' },
  N: { label: 'Mis notas y reflexiones', note: 'Lo que escribiste vos sobre ese material, o tu reflexión para esta pieza.' },
  M: { label: 'Herramienta de mi método', note: 'Entradas activas de tu biblioteca del método marcadas para Mi marca.' },
  E: { label: 'Datos del webinar', note: 'Lo que tiene el evento de Oike.' },
};
export const SLOT_ORIGINS = {
  mine: 'Escrito por vos',
  ai: 'Propuesta de IA (sin revisar)',
  ai_reviewed: 'Propuesta de IA, revisada por vos',
  ai_edited: 'Propuesta de IA, editada por vos',
  quote: 'Cita textual',
  event: 'Dato del evento',
};

export const BRAND_SELECTION_MAX = { items: 6, fragments: 12, tools: 4 };
export const BRAND_MATERIAL_MAX_CHARS = 12000;

// Words of a guaranteed promise or a universal recipe: flagged for Ale (never rewritten by the code)
export const PROMISE_RE = /\b(garantiza\w*|garantizad\w*|te aseguro|asegurad[oa]s?|100 ?%|sin falta|infalible\w*|f[oó]rmula (secreta|m[aá]gica|definitiva)|el secreto (para|de)|la receta (para|de)|siempre funciona|vas a lograr(lo)? s[ií] o s[ií]|en \d+ d[ií]as vas a)\b/gi;

export const slugify = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 50) || 'pieza';
export const exportFileName = (title, format, slide = null) => {
  const f = BRAND_FORMATS[format];
  return `${slugify(title)}-${format}${slide ? `-lamina-${slide}` : ''}-${f.width}x${f.height}.png`;
};
